// Favorites on the screen: per place ("me" here; cloud's Workspaces use their own
// place), one shared order of items and folders at the root.
//
// Promise: every change is shown at once and rolled back if the server refuses
// it; other windows follow through `favorite.changed`.
import {
  type QueryClient,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useApi } from "../app/context";
import { type Api, idempotencyKeyFor } from "../data/api";
import { newOptimisticId, optimisticMutation } from "../data/optimistic";
import { type FavoriteMove, moveFavorite, reorderBody } from "./favorites-move";

export interface FavoriteItem {
  type: "item";
  id: string;
  kind: string;
  targetId: string;
  folderId: string | null;
  position: number;
  title: string;
}

export interface FavoriteFolder {
  type: "folder";
  id: string;
  name: string;
  position: number;
  items: FavoriteItem[];
}

export interface FavoriteList {
  place: string;
  root: Array<FavoriteItem | FavoriteFolder>;
}

export const favoritesPrefix = ["me", "favorites"] as const;
export const favoritesKey = (place: string) =>
  [...favoritesPrefix, place] as const;

export const favoritesQuery = (api: Api, place: string) =>
  queryOptions({
    queryKey: favoritesKey(place),
    queryFn: () =>
      api<FavoriteList>(`/api/me/favorites?place=${encodeURIComponent(place)}`),
  });

export function useFavorites(place: string) {
  return useQuery(favoritesQuery(useApi(), place));
}

/** The favorite of a target, wherever it sits (root or a folder). */
export function findFavorite(
  list: FavoriteList | undefined,
  kind: string,
  targetId: string,
): FavoriteItem | undefined {
  for (const entry of list?.root ?? []) {
    const items = entry.type === "folder" ? entry.items : [entry];
    const hit = items.find((i) => i.kind === kind && i.targetId === targetId);
    if (hit) return hit;
  }
  return undefined;
}

const withoutItem = (list: FavoriteList, id: string): FavoriteList => ({
  ...list,
  root: list.root
    .filter((e) => !(e.type === "item" && e.id === id))
    .map((e) =>
      e.type === "folder"
        ? { ...e, items: e.items.filter((i) => i.id !== id) }
        : e,
    ),
});

export interface ToggleInput {
  kind: string;
  targetId: string;
  title: string;
  /** The favorite to remove; absent = add. */
  removeId?: string;
}

export function useToggleFavorite(place: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: ToggleInput) =>
      input.removeId
        ? api(`/api/me/favorites/${input.removeId}`, { method: "DELETE" })
        : api("/api/me/favorites", {
            method: "POST",
            body: { kind: input.kind, targetId: input.targetId },
            idempotencyKey: idempotencyKeyFor(input),
          }),
    ...optimisticMutation<FavoriteList, ToggleInput>(
      qc,
      favoritesKey(place),
      (old, input) => {
        if (!old) return old;
        if (input.removeId) return withoutItem(old, input.removeId);
        return {
          ...old,
          root: [
            ...old.root,
            {
              type: "item",
              id: newOptimisticId(),
              kind: input.kind,
              targetId: input.targetId,
              folderId: null,
              position: old.root.length,
              title: input.title,
            },
          ],
        };
      },
    ),
  });
}

export function useCreateFolder(place: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string }) =>
      api("/api/me/favorites/folders", {
        method: "POST",
        body: { place, name: input.name },
      }),
    ...optimisticMutation<FavoriteList, { name: string }>(
      qc,
      favoritesKey(place),
      (old, input) =>
        old && {
          ...old,
          root: [
            ...old.root,
            {
              type: "folder",
              id: newOptimisticId(),
              name: input.name,
              position: old.root.length,
              items: [],
            },
          ],
        },
    ),
  });
}

export function useRenameFolder(place: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; name: string }) =>
      api(`/api/me/favorites/folders/${input.id}`, {
        method: "PATCH",
        body: { name: input.name },
      }),
    ...optimisticMutation<FavoriteList, { id: string; name: string }>(
      qc,
      favoritesKey(place),
      (old, input) =>
        old && {
          ...old,
          root: old.root.map((e) =>
            e.type === "folder" && e.id === input.id
              ? { ...e, name: input.name }
              : e,
          ),
        },
    ),
  });
}

/** Deleting a folder moves its favorites to the end of the root, in their order. */
export function useDeleteFolder(place: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string }) =>
      api(`/api/me/favorites/folders/${input.id}`, { method: "DELETE" }),
    ...optimisticMutation<FavoriteList, { id: string }>(
      qc,
      favoritesKey(place),
      (old, input) => {
        if (!old) return old;
        const folder = old.root.find(
          (e): e is FavoriteFolder => e.type === "folder" && e.id === input.id,
        );
        return {
          ...old,
          root: [
            ...old.root.filter((e) => e !== folder),
            ...(folder?.items.map((i) => ({ ...i, folderId: null })) ?? []),
          ],
        };
      },
    ),
  });
}

/**
 * Moves a favorite or a folder (favorites-move.ts). Sends the order of the
 * container it went into, read from the list the person sees.
 */
export function useMoveFavorite(place: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: FavoriteMove) => {
      const list = qc.getQueryData<FavoriteList>(favoritesKey(place));
      // The cache already holds the moved list (onMutate runs first).
      return api("/api/me/favorites/order", {
        method: "PATCH",
        body: reorderBody(list ?? { place, root: [] }, input),
      });
    },
    ...optimisticMutation<FavoriteList, FavoriteMove>(
      qc,
      favoritesKey(place),
      (old, input) => old && moveFavorite(old, input),
    ),
  });
}

/** For realtime: any favorite change refetches every place's list. */
export function invalidateFavorites(qc: QueryClient) {
  return qc.invalidateQueries({ queryKey: favoritesPrefix });
}
